import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Header } from 'components/layout/header'
import { Main } from 'components/layout/main'
import { Search } from 'components/search'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { UsersDialogs } from 'features/users/components/users-dialogs'
import UsersProvider, { useUsers } from 'features/users/context/users-context'
import { GenericDataTable } from 'helpers/generic-data-table'
import { usersColumns } from 'helpers/user-columns'
import {
  ALL,
  FilterState,
  filterChips,
  initialFilters,
  QUICK_FILTERS,
  toParams,
  TriState,
  withoutChip,
} from 'helpers/user-filters'
import { Loader, SlidersHorizontal, X } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store/store'
import {
  banUser,
  requestPasswordReset,
  searchUsers,
  sendConfirmationMail,
  setPremiumExpirationDate,
  setPremiumUser,
  unbanUser,
  type UserRow,
} from 'store/users/usersSlice'
import { UsersFiltersSheet } from 'ui/components/UsersFiltersSheet'
import {
  UserMobileCard,
  type UserRowHandlers,
} from 'ui/components/users/UserCells'

const pageSize = 20

const percent = (count: number, total: number) =>
  total ? ` (${Math.round((count / total) * 100)}%)` : ''

const UsersPageContent: React.FC = () => {
  const dispatch = useAppDispatch()
  const { loading, error, users, totalPages, summary } = useAppSelector(
    (state) => state.users
  )
  const { setCurrentRow, setOpen } = useUsers()

  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [pageIndex, setPageIndex] = useState(0) // React Table uses 0-based index

  const handleOpenUserDialog = useCallback(
    (user: UserRow) => {
      setCurrentRow(user)
      setOpen('edit')
    },
    [setCurrentRow, setOpen]
  )

  const handleDelete = useCallback(
    (user: UserRow) => {
      setCurrentRow(user)
      setOpen('delete')
    },
    [setCurrentRow, setOpen]
  )

  const handlePasswordReset = useCallback(
    (email: string) => {
      dispatch(requestPasswordReset(email))
    },
    [dispatch]
  )

  // Debounced so typing in the search box doesn't fire a call per keystroke
  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(
        searchUsers({
          ...toParams(filters),
          page: pageIndex + 1, // the API is 1-based
          limit: pageSize,
          with_summary: true,
        })
      )
    }, 400)
    return () => clearTimeout(timeout)
  }, [dispatch, filters, pageIndex])

  // Every filter change goes back to the first page
  const apply = useCallback((next: FilterState) => {
    setFilters(next)
    setPageIndex(0)
  }, [])

  const handleBan = useCallback(
    (userId: number, notes: string) => {
      dispatch(banUser({ userId, notes }))
    },
    [dispatch]
  )

  const handleUnban = useCallback(
    (userId: number) => {
      dispatch(unbanUser(userId))
    },
    [dispatch]
  )

  const handleSetPremiumExpirationDate = useCallback(
    (userId: number, date: string) => {
      dispatch(setPremiumExpirationDate({ userId, date }))
    },
    [dispatch]
  )

  const handleSetPremium = useCallback(
    (userId: number) => {
      dispatch(setPremiumUser(userId))
    },
    [dispatch]
  )

  const handleSendEmailAgain = useCallback(
    (userMail: string) => {
      dispatch(sendConfirmationMail(userMail))
    },
    [dispatch]
  )

  // Memoized: new column definitions are new cell components, and React would
  // remount every cell on each store update. On mobile that closes the date
  // picker at the first change and swallows taps on the checkboxes.
  const handlers = useMemo<UserRowHandlers>(
    () => ({
      onOpen: handleOpenUserDialog,
      onDelete: handleDelete,
      onBan: handleBan,
      onUnban: handleUnban,
      onSetPremiumUser: handleSetPremium,
      onSetPremiumExpirationDate: handleSetPremiumExpirationDate,
      onSendConfirmationEmail: handleSendEmailAgain,
      onPasswordReset: handlePasswordReset,
    }),
    [
      handleOpenUserDialog,
      handleDelete,
      handleBan,
      handleUnban,
      handleSetPremium,
      handleSetPremiumExpirationDate,
      handleSendEmailAgain,
      handlePasswordReset,
    ]
  )
  const columns = useMemo(() => usersColumns(handlers), [handlers])
  const renderMobileRow = useCallback(
    (user: UserRow) => <UserMobileCard user={user} handlers={handlers} />,
    [handlers]
  )

  const chips = filterChips(filters)
  const sheetChips = chips.filter(
    (chip) => !QUICK_FILTERS.some(({ key }) => chip.keys.includes(key))
  )

  const toolbar = (
    <div className='my-4 space-y-3'>
      <div className='flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 p-3'>
        <Input
          type='text'
          placeholder='Nome, email, ID, tessera FIT, ordine Shopify, codice...'
          value={filters.query}
          onChange={(e) => apply({ ...filters, query: e.target.value })}
          className='max-w-md bg-background'
        />
        {QUICK_FILTERS.map(({ key, label }) => (
          <Select
            key={key}
            value={filters[key]}
            onValueChange={(value) =>
              apply({ ...filters, [key]: value as TriState })
            }
          >
            <SelectTrigger className='w-[170px] bg-background'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{label}: tutti</SelectItem>
              <SelectItem value='yes'>{label}: sì</SelectItem>
              <SelectItem value='no'>{label}: no</SelectItem>
            </SelectContent>
          </Select>
        ))}
        <Button
          variant='outline'
          className='ms-auto'
          onClick={() => setSheetOpen(true)}
        >
          <SlidersHorizontal className='h-4 w-4' />
          Altri filtri
          {sheetChips.length > 0 && (
            <Badge className='ms-1 px-1.5'>{sheetChips.length}</Badge>
          )}
        </Button>
      </div>

      <div className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
        {chips.map((chip) => (
          <button
            key={chip.keys.join()}
            type='button'
            onClick={() => apply(withoutChip(filters, chip))}
            className='inline-flex items-center gap-1.5 rounded-full border bg-background py-1 ps-3 pe-2 text-xs hover:bg-accent'
          >
            {chip.label}
            <X className='h-3 w-3' />
          </button>
        ))}
        {summary && (
          <span className='ms-auto'>
            <strong className='text-foreground'>{summary.total_count}</strong>{' '}
            utenti · premium attivi {summary.premium_count}
            {percent(summary.premium_count, summary.total_count)} · FITP{' '}
            {summary.fit_player_profile_count}
            {percent(summary.fit_player_profile_count, summary.total_count)} ·
            confermati {summary.account_confirmed_count}
            {percent(summary.account_confirmed_count, summary.total_count)} ·
            bannati {summary.banned_count}
          </span>
        )}
      </div>

      <UsersFiltersSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        filters={filters}
        onApply={apply}
      />
    </div>
  )

  // The toolbar stays mounted while loading, so the search box keeps its focus
  return (
    <>
      {toolbar}
      {loading ? (
        <Loader className='h-5 w-5 animate-spin' />
      ) : error ? (
        <p className='text-red-500'>Errore: {error}</p>
      ) : (
        <GenericDataTable
          columns={columns}
          data={users || []}
          pageCount={totalPages}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageIndexChange={setPageIndex}
          renderMobileRow={renderMobileRow}
          getRowKey={(user) => user.id}
        />
      )}
    </>
  )
}

const UsersPage: React.FC = () => {
  return (
    <UsersProvider>
      {/* ===== Top Heading ===== */}
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
        </div>
      </Header>

      {/* ===== Main ===== */}
      <Main>
        <div className='mb-2 flex flex-wrap items-center justify-between space-y-2 gap-x-4'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>Pagina Utenti</h2>
            <p className='text-muted-foreground'>Pagina di gestione utenti</p>
          </div>
        </div>
        <div className='-mx-4 mt-5 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-y-0 lg:space-x-12'>
          <UsersPageContent />
        </div>
      </Main>
      <UsersDialogs />
    </UsersProvider>
  )
}

export default UsersPage
